import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSnackbar } from 'notistack';
import { useApiQuery } from '../../../../api/hooks';
import apiClient from '../../../../api/apiClient';
import { usePreloaderStore } from '../../../../stores';
import { SendIcon } from '../../model/icons';
import LabeledSelect from '../controls/LabeledSelect';
import RecipientSegment from './RecipientSegment';
import FileDropzone from './FileDropzone';
import { RECIPIENT_TYPES } from './recipientsModel';
import './broadcast.css';

const MAX_FILES = 10;
const MAX_CAPTION = 1024;
const USE_MARKDOWN = true;
const BROADCAST_URL = '/api/camps/admin/broadcast';

// Раздел «Рассылки»: отправка сообщения смене/команде/студенту.
// Каналы: Telegram + Web Push (бэк дублирует caption пуш-подписчикам PWA) — студент без TG тоже получатель.
export default function BroadcastContent({ shift }) {
    const { enqueueSnackbar } = useSnackbar();
    const { show, hide } = usePreloaderStore();
    const smenaId = shift?.id;

    const [recipientType, setRecipientType] = useState(RECIPIENT_TYPES.SMENA);
    const [recipientId, setRecipientId] = useState(null);
    const [caption, setCaption] = useState('');
    const [files, setFiles] = useState([]);
    const [loading, setLoading] = useState(false);

    const { data: teams = [] } = useApiQuery(
        ['teams', smenaId],
        `/api/camps/public/teams/by-smena?smenaId=${smenaId}`,
        { enabled: !!smenaId }
    );
    const { data: students = [] } = useApiQuery(
        ['students-broadcast', smenaId],
        `/api/camps/admin/students?smenaId=${smenaId}`,
        { enabled: !!smenaId }
    );

    useEffect(() => { setRecipientId(null); }, [recipientType]);

    const studentsWithTelegram = students.filter(s => s.hasTelegram).length;
    const studentsWithoutTelegram = students.length - studentsWithTelegram;
    // hasPush появится в /admin/students после доработки бэка — UI подхватит его автоматически.
    const withPushData = students.some(s => s.hasPush != null);
    const studentsWithPush = students.filter(s => s.hasPush).length;

    const channelSuffix = (s) => {
        if (withPushData) {
            const channels = [s.hasTelegram && 'TG', s.hasPush && 'Push'].filter(Boolean);
            return channels.length ? ` · ${channels.join('+')}` : ' · нет каналов';
        }
        return s.hasTelegram ? '' : ' · без TG (только Web Push)';
    };

    const teamCounts = useMemo(() => {
        const counts = {};
        teams.forEach(t => {
            const members = students.filter(s => s.teamId === t.id);
            counts[t.id] = { total: members.length, tg: members.filter(s => s.hasTelegram).length };
        });
        return counts;
    }, [teams, students]);

    const teamOptions = teams.map(t => {
        const c = teamCounts[t.id] || { total: 0, tg: 0 };
        return { key: t.id, value: `${t.name} (${c.total} чел., TG: ${c.tg})` };
    });
    const studentOptions = [...students]
        .sort((a, b) => a.surname.localeCompare(b.surname))
        .map(s => ({
            key: s.id,
            value: `${s.surname} ${s.name} (${s.teamName || 'без команды'})${channelSuffix(s)}`
        }));

    const handleAddFiles = (selected) => {
        if (selected.length + files.length > MAX_FILES) {
            enqueueSnackbar(`Максимум ${MAX_FILES} файлов`, { variant: 'warning' });
            return;
        }
        setFiles(prev => [...prev, ...selected]);
    };

    const handleRemoveFile = (index) => setFiles(files.filter((_, i) => i !== index));

    const handleSelectRecipient = (e) => setRecipientId(e.target.value ? Number(e.target.value) : null);

    const captionOver = caption.length > MAX_CAPTION;
    const canSend = (caption.trim().length > 0 || files.length > 0) && !captionOver;

    const handleSubmit = async () => {
        let finalRecipientId;
        if (recipientType === RECIPIENT_TYPES.SMENA) {
            finalRecipientId = smenaId;
        } else if (!recipientId) {
            enqueueSnackbar('Выберите получателя', { variant: 'warning' });
            return;
        } else {
            finalRecipientId = recipientId;
        }

        setLoading(true);
        show();
        try {
            const formData = new FormData();
            const request = {
                recipientType,
                recipientId: finalRecipientId,
                caption: caption.trim() || undefined,
                markdown: USE_MARKDOWN
            };
            formData.append('request', new Blob([JSON.stringify(request)], { type: 'application/json' }));
            files.forEach(file => formData.append('files', file));

            const { data: result } = await apiClient.post(BROADCAST_URL, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            if (result.status === 'NO_RECIPIENTS' || result.totalRecipients === 0) {
                enqueueSnackbar('Нет получателей с каналами уведомлений (Telegram или Web Push)', { variant: 'warning' });
            } else if (result.status === 'PROCESSING') {
                enqueueSnackbar(`Рассылка отправляется ${result.totalRecipients} получателям`, { variant: 'success' });
            } else if (result.status === 'COMPLETED' && result.failedCount > 0) {
                enqueueSnackbar(`Отправлено ${result.sentCount} из ${result.totalRecipients}. Ошибок: ${result.failedCount}`, { variant: 'warning' });
            } else {
                enqueueSnackbar(`Отправлено ${result.totalRecipients} получателям`, { variant: 'success' });
            }

            setCaption('');
            setFiles([]);
        } catch (error) {
            enqueueSnackbar(error.response?.data?.message || 'Ошибка отправки', { variant: 'error' });
        } finally {
            setLoading(false);
            hide();
        }
    };

    return (
        <section className="content white">
            <div className="bc-wrap">
                <h2 className="bc-title">Рассылка сообщений</h2>

                <div className="bc-stats">
                    <SendIcon />
                    <span>
                        Получателей: <b>{students.length}</b>
                        &nbsp;|&nbsp; с Telegram: <b>{studentsWithTelegram}</b>
                        {withPushData && <> &nbsp;|&nbsp; с Web Push: <b>{studentsWithPush}</b></>}
                        {!withPushData && studentsWithoutTelegram > 0 && <> &nbsp;|&nbsp; без Telegram: <b>{studentsWithoutTelegram}</b></>}
                    </span>
                </div>
                <div className="bc-hint">
                    Сообщение уходит в Telegram и дублируется Web Push подписчикам приложения — студенты без Telegram
                    тоже получат его, если разрешили уведомления. Файлы доставляются только в Telegram.
                </div>

                <label className="ctl-label">Кому отправить</label>
                <RecipientSegment value={recipientType} onChange={setRecipientType} smenaCount={students.length} />

                {recipientType === RECIPIENT_TYPES.TEAM && (
                    <LabeledSelect
                        className="bc-field"
                        placeholder="Выберите команду"
                        options={teamOptions}
                        value={recipientId || ''}
                        onChange={handleSelectRecipient}
                    />
                )}
                {recipientType === RECIPIENT_TYPES.STUDENT && (
                    <LabeledSelect
                        className="bc-field"
                        placeholder="Выберите студента"
                        options={studentOptions}
                        value={recipientId || ''}
                        onChange={handleSelectRecipient}
                    />
                )}

                <div className="bc-field">
                    <label className="ctl-label">Текст сообщения</label>
                    <textarea
                        className="textarea"
                        style={{ minHeight: 150 }}
                        value={caption}
                        onChange={(e) => setCaption(e.target.value)}
                        placeholder="Поддерживается Markdown: *жирный*, _курсив_, `код`"
                    />
                    <div className={`bc-counter${captionOver ? ' over' : ''}`}>{caption.length}/{MAX_CAPTION}</div>
                </div>

                <FileDropzone files={files} onAdd={handleAddFiles} onRemove={handleRemoveFile} />

                <button
                    type="button"
                    className={`bc-send${canSend && !loading ? ' ready' : ''}`}
                    onClick={handleSubmit}
                    disabled={!canSend || loading}
                >
                    <SendIcon />
                    <span>{loading ? 'Отправка...' : 'Отправить'}</span>
                </button>
            </div>
        </section>
    );
}

BroadcastContent.propTypes = {
    shift: PropTypes.shape({ id: PropTypes.number }).isRequired
};
